import { apiClient } from "../api/client";
import { writeError } from "./rfqItems";
import type {
  SavedRemarkRow,
  SavedRemarkInsert,
  SavedRemarkUpdate,
  SavedRemarkFileRow,
  SavedRemarkFileInsert,
} from "../types/savedRemarks";

// Writes to saved_remarks and saved_remark_files metadata, through the backend
// (validated and audited) instead of the anon key. Each returns
// { data, error } like the Supabase calls it replaced, so callers keep their
// error handling. The backend writes exactly the columns sent.

type Result<T> = { data: T | null; error: Error | null };

type Body = Record<string, unknown>;

async function send<T>(request: () => Promise<{ data: Body }>, pick: (body: Body) => T): Promise<Result<T>> {
  try {
    const response = await request();
    return { data: pick(response.data), error: null };
  } catch (err) {
    return { data: null, error: writeError(err) };
  }
}

export const insertSavedRemark = (insert: SavedRemarkInsert) =>
  send<SavedRemarkRow>(() => apiClient.post("/saved-remarks", insert), (body) => body.remark as SavedRemarkRow);

export const updateSavedRemark = (id: number, update: SavedRemarkUpdate) =>
  send<SavedRemarkRow>(() => apiClient.put(`/saved-remarks/${id}`, update), (body) => body.remark as SavedRemarkRow);

/** Soft delete (is_active = false). */
export const deactivateSavedRemark = (id: number) =>
  send<null>(() => apiClient.delete(`/saved-remarks/${id}`), () => null);

export const insertSavedRemarkFile = (insert: SavedRemarkFileInsert) =>
  send<SavedRemarkFileRow>(
    () => apiClient.post(`/saved-remarks/${insert.saved_remark_id}/files`, insert),
    (body) => body.file as SavedRemarkFileRow,
  );

/** Soft delete of the file record (is_active = false). */
export const deactivateSavedRemarkFile = (fileId: number) =>
  send<null>(() => apiClient.delete(`/saved-remarks/files/${fileId}`), () => null);

/**
 * Count one use of a saved remark (POST /saved-remarks/track-usage). The
 * backend increments the stored count; data is the updated row, or null when
 * it could not be counted.
 */
export const trackSavedRemarkUsage = (id: number) =>
  send<SavedRemarkRow | null>(
    () => apiClient.post("/saved-remarks/track-usage", { savedRemarkId: id, wasAccepted: true }),
    (body) => (body.remark as SavedRemarkRow | undefined) ?? null,
  );

/**
 * Record whether a remark suggestion was accepted (the suggestions panel's
 * own tracking, POST /saved-remarks/track-usage). Rejects with the raw error.
 */
export const trackRemarkSuggestionUsage = (savedRemarkId: number, rfqItemId: number | undefined, wasAccepted: boolean) =>
  apiClient.post("/saved-remarks/track-usage", {
    savedRemarkId,
    rfqItemId,
    wasAccepted,
  });

/**
 * Record a price-research remark being suggested or used
 * (POST /remark-suggestions/track). Rejects with the raw error.
 */
export const trackPriceResearchRemark = (source: string, sourceId: string | number, event: "suggested" | "used") =>
  apiClient.post("/remark-suggestions/track", {
    source,
    sourceId,
    event,
  });
